/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  KEYPOINT_NAMES,
  KEYPOINT_LABELS,
  SKELETON_BONES,
} from './skeletonDefs';
import { drawSkeleton, hitTestKeypoint, hitTestBone } from './drawSkeleton';
import { usePlayer } from './usePlayer';
import ProgressBar from './ProgressBar';
import './KeypointPlayer.less';

const KEYPOINT_VISIBLE_THRESHOLD = 0.1;
const LOW_CONFIDENCE_THRESHOLD = 0.2;
const BASE_SMOOTH_ALPHA = 0.32;
const FAST_MOTION_ALPHA = 0.55;
const LARGE_MOTION_ALPHA = 0.72;

const getKeypointScore = (kp) => kp?.s ?? kp?.score ?? 0;
const isVisibleKeypoint = (kp) =>
  getKeypointScore(kp) >= KEYPOINT_VISIBLE_THRESHOLD;

const cloneKeypoint = (kp = {}) => ({
  id: kp.id,
  name: kp.name,
  label: kp.label,
  x: kp.x ?? 0,
  y: kp.y ?? 0,
  s: kp.s ?? kp.score ?? 0,
  color: kp.color,
});

const cloneFrame = (frame = {}) => {
  const keypoints = frame.keypoints;
  const resultKeypoints = new Array(keypoints.length);
  for (let i = 0; i < keypoints.length; i++) {
    resultKeypoints[i] = cloneKeypoint(keypoints[i]);
  }
  return { ...frame, keypoints: resultKeypoints };
};

const getAdaptiveAlpha = (prev, next) => {
  const distance = Math.hypot(
    (next.x ?? 0) - (prev.x ?? 0),
    (next.y ?? 0) - (prev.y ?? 0),
  );
  if (distance >= 80) return LARGE_MOTION_ALPHA;
  if (distance >= 36) return FAST_MOTION_ALPHA;
  return BASE_SMOOTH_ALPHA;
};

const interpolateLowConfidenceKeypoints = (frames) => {
  if (!Array.isArray(frames) || frames.length < 3) return frames;
  const kpCount = frames[0]?.keypoints?.length || 0;
  if (!kpCount) return frames;
  const result = frames.map(cloneFrame);
  const highConfidenceIndices = Array(kpCount)
    .fill(null)
    .map(() => []);
  for (let kpIdx = 0; kpIdx < kpCount; kpIdx++) {
    for (let i = 0; i < result.length; i++) {
      const kp = result[i].keypoints[kpIdx];
      if (isVisibleKeypoint(kp) && kp.s >= LOW_CONFIDENCE_THRESHOLD) {
        highConfidenceIndices[kpIdx].push(i);
      }
    }
  }
  for (let kpIdx = 0; kpIdx < kpCount; kpIdx++) {
    const highIndices = highConfidenceIndices[kpIdx];
    if (highIndices.length === 0) continue;
    for (let i = 0; i < result.length; i++) {
      const kp = result[i].keypoints[kpIdx];
      if (!isVisibleKeypoint(kp) || kp.s >= LOW_CONFIDENCE_THRESHOLD) continue;
      let prevIdx = -1,
        nextIdx = -1;
      for (let j = highIndices.length - 1; j >= 0; j--) {
        if (highIndices[j] < i) {
          prevIdx = highIndices[j];
          break;
        }
      }
      for (let j = 0; j < highIndices.length; j++) {
        if (highIndices[j] > i) {
          nextIdx = highIndices[j];
          break;
        }
      }
      if (prevIdx >= 0 && nextIdx >= 0) {
        const prevKp = result[prevIdx].keypoints[kpIdx];
        const nextKp = result[nextIdx].keypoints[kpIdx];
        const t = (i - prevIdx) / (nextIdx - prevIdx);
        result[i].keypoints[kpIdx] = {
          ...kp,
          x: prevKp.x + (nextKp.x - prevKp.x) * t,
          y: prevKp.y + (nextKp.y - prevKp.y) * t,
        };
      } else if (prevIdx >= 0) {
        const prevKp = result[prevIdx].keypoints[kpIdx];
        result[i].keypoints[kpIdx] = { ...kp, x: prevKp.x, y: prevKp.y };
      } else if (nextIdx >= 0) {
        const nextKp = result[nextIdx].keypoints[kpIdx];
        result[i].keypoints[kpIdx] = { ...kp, x: nextKp.x, y: nextKp.y };
      }
    }
  }
  return result;
};

const smoothFrameSequence = (frames) => {
  if (!Array.isArray(frames) || frames.length === 0) return [];
  if (frames.length === 1) return [cloneFrame(frames[0])];
  const globalMotions = new Array(frames.length);
  globalMotions[0] = 0;
  for (let i = 1; i < frames.length; i++) {
    const prevKps = frames[i - 1].keypoints;
    const currKps = frames[i].keypoints;
    let totalDist = 0,
      count = 0;
    for (let j = 0; j < currKps.length; j++) {
      const kp = currKps[j],
        prev = prevKps[j];
      if (isVisibleKeypoint(kp) && prev && isVisibleKeypoint(prev)) {
        totalDist += Math.hypot(kp.x - prev.x, kp.y - prev.y);
        count++;
      }
    }
    globalMotions[i] = count > 0 ? totalDist / count : 0;
  }
  const clonedFrames = frames.map(cloneFrame);
  const result = new Array(frames.length);
  for (let i = 0; i < frames.length; i++) {
    const currFrame = clonedFrames[i];
    const currKeypoints = currFrame.keypoints;
    const prevKeypoints = i > 0 ? clonedFrames[i - 1].keypoints : null;
    const nextKeypoints =
      i < frames.length - 1 ? clonedFrames[i + 1].keypoints : null;
    const motion = Math.max(
      globalMotions[i],
      globalMotions[Math.min(i + 1, frames.length - 1)] || 0,
    );
    let currW, neighborW;
    if (motion >= 80) {
      currW = 0.7;
      neighborW = 0.15;
    } else if (motion >= 36) {
      currW = 0.55;
      neighborW = 0.225;
    } else {
      currW = 0.5;
      neighborW = 0.25;
    }
    const smoothedKeypoints = new Array(currKeypoints.length);
    for (let j = 0; j < currKeypoints.length; j++) {
      const current = currKeypoints[j];
      if (!isVisibleKeypoint(current)) {
        smoothedKeypoints[j] = current;
        continue;
      }
      const prev = prevKeypoints ? prevKeypoints[j] : null;
      const next = nextKeypoints ? nextKeypoints[j] : null;
      const hasPrev = prev && isVisibleKeypoint(prev);
      const hasNext = next && isVisibleKeypoint(next);
      if (hasPrev && hasNext) {
        smoothedKeypoints[j] = {
          id: current.id,
          name: current.name,
          label: current.label,
          x: prev.x * neighborW + current.x * currW + next.x * neighborW,
          y: prev.y * neighborW + current.y * currW + next.y * neighborW,
          s: current.s,
          color: current.color,
        };
      } else if (hasPrev) {
        const totalW = currW + neighborW;
        smoothedKeypoints[j] = {
          id: current.id,
          name: current.name,
          label: current.label,
          x: (prev.x * neighborW + current.x * currW) / totalW,
          y: (prev.y * neighborW + current.y * currW) / totalW,
          s: current.s,
          color: current.color,
        };
      } else if (hasNext) {
        const totalW = currW + neighborW;
        smoothedKeypoints[j] = {
          id: current.id,
          name: current.name,
          label: current.label,
          x: (current.x * currW + next.x * neighborW) / totalW,
          y: (current.y * currW + next.y * neighborW) / totalW,
          s: current.s,
          color: current.color,
        };
      } else {
        smoothedKeypoints[j] = current;
      }
    }
    result[i] = { ...currFrame, keypoints: smoothedKeypoints };
  }
  return result;
};

const interpolateKeypoints = (
  currentPoints = [],
  nextPoints = [],
  progress = 0,
) => {
  const t = Math.max(0, Math.min(progress, 1));
  const result = new Array(currentPoints.length);
  for (let i = 0; i < currentPoints.length; i++) {
    const current = currentPoints[i];
    const next = nextPoints[i] || current;
    if (!isVisibleKeypoint(current) || !isVisibleKeypoint(next)) {
      result[i] = current;
      continue;
    }
    result[i] = {
      id: current.id,
      name: current.name,
      label: current.label,
      x: current.x + (next.x - current.x) * t,
      y: current.y + (next.y - current.y) * t,
      s: current.s + (next.s - current.s) * t,
      color: current.color,
    };
  }
  return result;
};

const formatDuration = (seconds) => {
  if (!seconds) return '0s';
  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(1);
  return mins > 0 ? `${mins}m${secs}s` : `${secs}s`;
};

// 计算关键点边界框
const computeKeypointsBBox = (keypoints) => {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let hasValid = false;
  for (let i = 0; i < keypoints.length; i++) {
    const kp = keypoints[i];
    if (!isVisibleKeypoint(kp)) continue;
    hasValid = true;
    if (kp.x < minX) minX = kp.x;
    if (kp.x > maxX) maxX = kp.x;
    if (kp.y < minY) minY = kp.y;
    if (kp.y > maxY) maxY = kp.y;
  }
  if (!hasValid) return null;
  const width = maxX - minX || 1;
  const height = maxY - minY || 1;
  // 增加 50% 边距，让骨骼在画布中有充足留白
  const padX = width * 0.8;
  const padY = height * 0.8;
  return {
    minX: minX - padX,
    maxX: maxX + padX,
    minY: minY - padY,
    maxY: maxY + padY,
    width: width + padX * 2,
    height: height + padY * 2,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  };
};

// 等待布局稳定（双 requestAnimationFrame，确保浏览器完成完整布局）
const waitForLayout = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

const KeypointPlayer = forwardRef(
  (
    { dataUrl, title = '', autoPlay = true, loop = false, onlyShow = false },
    ref,
  ) => {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const [metaData, setMetaData] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [highlightedItems, setHighlightedItems] = useState(new Set());
    const [downloading, setDownloading] = useState(false);
    const [tooltip, setTooltip] = useState({
      show: false,
      text: '',
      style: {},
    });

    const frameMetaRef = useRef(null);
    const processedFramesRef = useRef([]);
    const cachedRenderMetaRef = useRef(null);
    const cachedCanvasWidthRef = useRef(0);
    const cachedCanvasHeightRef = useRef(0);
    const resizeObserverRef = useRef(null);
    const resizeTimerRef = useRef(null);

    const player = usePlayer();

    const highlightedList = useMemo(() => {
      const list = [];
      highlightedItems.forEach((id) => {
        if (id.startsWith('kp:')) {
          const idx = parseInt(id.substring(3));
          list.push({
            id,
            label: KEYPOINT_LABELS[KEYPOINT_NAMES[idx]] || KEYPOINT_NAMES[idx],
            type: 'kp',
          });
        } else if (id === 'head') {
          list.push({ id, label: '头部', type: 'bone' });
        } else if (id.startsWith('bone:')) {
          const boneName = id.substring(5);
          if (boneName === 'HEAD-NECK')
            list.push({ id, label: '头-颈', type: 'bone' });
          else if (boneName === 'NECK-HIP')
            list.push({ id, label: '颈-躯干', type: 'bone' });
          else if (
            boneName === 'LEFT_SHOULDER-LEFT_HIP' ||
            boneName === 'RIGHT_SHOULDER-RIGHT_HIP'
          )
            return;
          else {
            const bone = SKELETON_BONES.find((b) => b[2] === boneName);
            if (bone)
              list.push({
                id,
                label: `${KEYPOINT_LABELS[KEYPOINT_NAMES[bone[0]]]}-${
                  KEYPOINT_LABELS[KEYPOINT_NAMES[bone[1]]]
                }`,
                type: 'bone',
              });
          }
        }
      });
      return list;
    }, [highlightedItems]);

    const initCanvasSize = () => {
      if (!containerRef.current) return null;
      const rect = containerRef.current.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return null;
      if (!canvasRef.current) return false;
      const dpr = window.devicePixelRatio || 1;
      canvasRef.current.width = Math.floor(rect.width) * dpr;
      canvasRef.current.height = Math.floor(rect.height) * dpr;
      cachedRenderMetaRef.current = null;
      cachedCanvasWidthRef.current = 0;
      cachedCanvasHeightRef.current = 0;
      return true;
    };

    const renderFrameCallback = (frame, frameIndex, currentTime) => {
      if (!frame || !canvasRef.current) return;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const processedFrames = processedFramesRef.current;
      const idx = frameIndex ?? player.currentFrameIndex;
      const time = currentTime ?? player.currentTime;
      const currTs = frame.ts ?? 0;
      const nextFrame = processedFrames[idx + 1];
      let progress = 0;
      if (nextFrame) {
        const nextTs = nextFrame.ts ?? currTs;
        const span = nextTs - currTs;
        if (span > 0)
          progress = Math.max(0, Math.min(1, (time - currTs) / span));
      }
      const interpolatedKeypoints =
        nextFrame && progress > 0.001
          ? interpolateKeypoints(frame.keypoints, nextFrame.keypoints, progress)
          : frame.keypoints;
      const cW = canvas.width;
      const cH = canvas.height;
      const dpr = window.devicePixelRatio || 1;
      const logW = cW / dpr;
      const logH = cH / dpr;
      const canvasSizeChanged =
        cW !== cachedCanvasWidthRef.current ||
        cH !== cachedCanvasHeightRef.current;
      let renderMeta = cachedRenderMetaRef.current;
      if (!renderMeta || canvasSizeChanged) {
        const dataW = metaData?.width || 3840;
        const dataH = metaData?.height || 2160;
        // 基于骨骼点实际边界框计算缩放，而非整个帧分辨率
        const bbox = computeKeypointsBBox(interpolatedKeypoints);
        const bboxW = bbox ? bbox.width : dataW;
        const bboxH = bbox ? bbox.height : dataH;
        const scale = Math.min(logW / bboxW, logH / bboxH);
        // 偏移：将整个 bbox 居中
        const bboxCx = bbox ? bbox.centerX : dataW / 2;
        const bboxCy = bbox ? bbox.centerY : dataH / 2;
        const offsetX = logW / 2 - bboxCx * scale;
        const offsetY = logH / 2 - bboxCy * scale;
        renderMeta = { dpr, scale, offsetX, offsetY, logW, logH, cW, cH };
        // bbox 为 null 时（首帧无有效关键点）不缓存，
        // 等后续帧有真实骨骼数据后重新计算正确的缩放比
        if (bbox) {
          cachedRenderMetaRef.current = renderMeta;
          cachedCanvasWidthRef.current = cW;
          cachedCanvasHeightRef.current = cH;
        }
      }
      const { scale, offsetX, offsetY } = renderMeta;
      const displaySize = Math.min(logW, logH);
      ctx.resetTransform();
      ctx.clearRect(0, 0, cW, cH);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, cW, cH);
      ctx.save();
      ctx.scale(dpr, dpr);
      ctx.translate(offsetX, offsetY);
      ctx.scale(scale, scale);
      drawSkeleton(
        ctx,
        interpolatedKeypoints,
        highlightedItems,
        scale,
        displaySize,
      );
      ctx.restore();
      frameMetaRef.current = {
        keypoints: interpolatedKeypoints,
        scale,
        offsetX,
        offsetY,
        dpr,
      };
    };

    const clientToData = (e) => {
      const frameMeta = frameMetaRef.current;
      if (!frameMeta || !canvasRef.current) return null;
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const dpr = frameMeta.dpr;
      const { scale, offsetX, offsetY } = frameMeta;
      const logX =
        ((e.clientX - rect.left) * (canvas.width / dpr)) / rect.width;
      const logY =
        ((e.clientY - rect.top) * (canvas.height / dpr)) / rect.height;
      const dataX = (logX - offsetX) / scale;
      const dataY = (logY - offsetY) / scale;
      return { dataX, dataY };
    };

    const handleCanvasClick = (e) => {
      if (onlyShow) return;
      const pos = clientToData(e);
      if (!pos) return;
      const frameMeta = frameMetaRef.current;
      let hitId = hitTestKeypoint(
        pos.dataX,
        pos.dataY,
        frameMeta.keypoints,
        frameMeta.scale,
      );
      if (!hitId)
        hitId = hitTestBone(pos.dataX, pos.dataY, frameMeta.keypoints);
      if (hitId) {
        setHighlightedItems((prev) => {
          const newSet = new Set(prev);
          newSet.has(hitId) ? newSet.delete(hitId) : newSet.add(hitId);
          return newSet;
        });
      } else {
        player.togglePlay();
      }
    };

    const handleMouseMove = (e) => {
      const pos = clientToData(e);
      if (!pos) {
        setTooltip({ show: false, text: '', style: {} });
        return;
      }
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const frameMeta = frameMetaRef.current;
      let hitId = hitTestKeypoint(
        pos.dataX,
        pos.dataY,
        frameMeta.keypoints,
        frameMeta.scale,
      );
      if (!hitId)
        hitId = hitTestBone(pos.dataX, pos.dataY, frameMeta.keypoints);
      if (hitId) {
        let label = '';
        if (hitId.startsWith('kp:')) {
          const idx = parseInt(hitId.substring(3));
          label = KEYPOINT_LABELS[KEYPOINT_NAMES[idx]] || KEYPOINT_NAMES[idx];
        } else if (hitId === 'head') {
          label = '头部';
        } else if (hitId.startsWith('bone:')) {
          const boneName = hitId.substring(5);
          if (boneName === 'HEAD-NECK') label = '头-颈';
          else if (boneName === 'NECK-HIP') label = '颈-躯干';
          else {
            const bone = SKELETON_BONES.find((b) => b[2] === boneName);
            if (bone)
              label = `${KEYPOINT_LABELS[KEYPOINT_NAMES[bone[0]]]}-${
                KEYPOINT_LABELS[KEYPOINT_NAMES[bone[1]]]
              }`;
          }
        }
        setTooltip({
          show: true,
          text: label,
          style: {
            left: `${e.clientX - rect.left + 12}px`,
            top: `${e.clientY - rect.top - 28}px`,
          },
        });
      } else {
        setTooltip({ show: false, text: '', style: {} });
      }
    };

    const handleSeek = (time) => {
      const wasPlaying = player.isPlaying;
      if (wasPlaying) player.pause();
      player.seekToTime(time);
      if (wasPlaying) player.play();
    };

    useImperativeHandle(
      ref,
      () => ({
        seekToTime: (time) => {
          player.seekToTime(time);
        },
        seekToFrame: (frameIndex) => {
          player.seekToFrame(frameIndex);
        },
      }),
      [player],
    );

    const toggleHighlightItem = (id) => {
      setHighlightedItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    };

    const clearHighlights = () => {
      setHighlightedItems(new Set());
    };

    const loadData = async () => {
      if (!dataUrl) return;
      setLoading(true);
      setLoaded(false);
      try {
        player.pause();
        const res = await fetch(dataUrl);
        const text = await res.text();
        const lines = text.trim().split('\n');
        const parsedFrames = [];
        let meta = null;
        lines.forEach((line) => {
          if (!line.trim()) return;
          const obj = JSON.parse(line);
          if (obj.type === 'metadata') {
            const v = obj.source || obj.video || {};
            meta = {
              width: v.width,
              height: v.height,
              fps: v.fps,
              frameCount: v.frameCount,
              duration: v.duration,
            };
          } else if (obj.type === 'frame') {
            if (Array.isArray(obj.keypoints)) {
              obj.keypoints = obj.keypoints.map((kp, idx) => ({
                id: idx,
                name: KEYPOINT_NAMES[idx] || `point_${idx}`,
                label: KEYPOINT_LABELS[KEYPOINT_NAMES[idx]] || `关键点${idx}`,
                x: kp[0],
                y: kp[1],
                s: kp[2] ?? 1.0,
              }));
            }
            parsedFrames.push(obj);
          } else if (obj.frameIndex !== undefined) {
            if (Array.isArray(obj.keypoints)) {
              obj.keypoints = obj.keypoints.map((kp, idx) => ({
                id: idx,
                name: KEYPOINT_NAMES[idx] || `point_${idx}`,
                label: KEYPOINT_LABELS[KEYPOINT_NAMES[idx]] || `关键点${idx}`,
                x: kp[0],
                y: kp[1],
                s: kp[2] ?? 1.0,
              }));
            }
            parsedFrames.push(obj);
          } else if (!meta) {
            meta = obj;
          }
        });
        parsedFrames.sort((a, b) => a.frameIndex - b.frameIndex);
        const interpolated = interpolateLowConfidenceKeypoints(parsedFrames);
        processedFramesRef.current = smoothFrameSequence(interpolated);
        setMetaData(meta);
        player.init(
          processedFramesRef.current,
          meta?.fps || 25,
          meta?.duration,
        );
        player.setRenderCallback(renderFrameCallback);
        setLoaded(true);
        await waitForLayout();
        if (initCanvasSize()) {
          player.renderFrame(0);
          if (autoPlay) player.play();
        }
      } catch (err) {
        console.error('加载关键点数据失败:', err);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      if (canvasRef.current) {
        canvasRef.current.addEventListener('click', handleCanvasClick);
        canvasRef.current.addEventListener('mousemove', handleMouseMove);
        canvasRef.current.addEventListener('mouseleave', () =>
          setTooltip({ show: false, text: '', style: {} }),
        );
      }
      if (containerRef.current) {
        resizeObserverRef.current = new ResizeObserver(() => {
          clearTimeout(resizeTimerRef.current);
          resizeTimerRef.current = setTimeout(() => {
            if (!player.frames.length) return;
            if (initCanvasSize()) {
              const frameIdx = player.currentFrameIndex;
              if (player.frames[frameIdx]) {
                renderFrameCallback(
                  player.frames[frameIdx],
                  frameIdx,
                  player.currentTime,
                );
              }
            }
          }, 80);
        });
        resizeObserverRef.current.observe(containerRef.current);
      }
      loadData();
      return () => {
        player.pause();
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }
        clearTimeout(resizeTimerRef.current);
        if (canvasRef.current) {
          canvasRef.current.removeEventListener('click', handleCanvasClick);
          canvasRef.current.removeEventListener('mousemove', handleMouseMove);
        }
      };
    }, []);

    useEffect(() => {
      if (dataUrl) {
        setHighlightedItems(new Set());
        frameMetaRef.current = null;
        processedFramesRef.current = [];
        loadData();
      }
    }, [dataUrl]);

    useEffect(() => {
      if (player.isEnded && (loop || onlyShow)) {
        player.replay();
      }
    }, [player.isEnded]);

    return (
      <div className={`keypoint-player ${onlyShow ? 'only-show' : ''}`}>
        {!onlyShow && (title || metaData) && (
          <div className="player-header">
            {title && <div className="player-title">{title}</div>}
            {metaData && (
              <div className="player-info">
                <span>
                  {metaData.width}×{metaData.height}
                </span>
                <span>{metaData.fps}fps</span>
                <span>{metaData.frameCount}帧</span>
                <span>{formatDuration(metaData.duration)}</span>
              </div>
            )}
          </div>
        )}
        <div className="player-canvas-wrap" ref={containerRef}>
          <canvas ref={canvasRef} className="player-canvas" />
          {tooltip.show && !onlyShow && (
            <div className="player-tooltip" style={tooltip.style}>
              {tooltip.text}
            </div>
          )}
          {!loaded && loading && (
            <div className="overlay-loading">加载中...</div>
          )}
          {highlightedList.length > 0 && !onlyShow && (
            <div className="highlighted-list">
              <div className="highlighted-title">
                <span>已高亮 ({highlightedList.length})</span>
                <button className="clear-btn" onClick={clearHighlights}>
                  清除
                </button>
              </div>
              <div className="highlighted-items">
                {highlightedList.map((item) => (
                  <div
                    key={item.id}
                    className="highlighted-item"
                    onClick={() => toggleHighlightItem(item.id)}
                  >
                    <span className="item-dot" />
                    {item.label}
                    <span className="item-close">&times;</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {loaded && !onlyShow && (
          <div className="player-controls">
            <button className="control-btn" onClick={() => player.togglePlay()}>
              {!player.isPlaying ? (
                <svg viewBox="0 0 1024 1024" width="16" height="16">
                  <path
                    fill="currentColor"
                    d="M741.042 458.006l-414.933-248.96C291.627 188.331 256 213.12 256 256v497.92c0 42.88 35.627 67.669 70.101 46.955l414.934-248.96c34.474-20.715 34.474-73.195 0-93.909z"
                  />
                </svg>
              ) : (
                <svg viewBox="0 0 1024 1024" width="16" height="16">
                  <path
                    fill="currentColor"
                    d="M320 256h128v512H320zM576 256h128v512H576z"
                  />
                </svg>
              )}
            </button>
            <div className="progress-wrap">
              <ProgressBar
                currentTime={player.currentTime}
                duration={player.duration}
                onSeek={handleSeek}
              />
            </div>
            <button
              className={`download-btn ${downloading ? 'downloading' : ''}`}
              disabled={downloading}
              onClick={() => {}}
              title="下载为视频文件"
            >
              {!downloading ? (
                <svg viewBox="0 0 1024 1024" width="16" height="16">
                  <path
                    fill="currentColor"
                    d="M504 612.8V64h96v548.8L770.4 448l67.2 67.2L512 832 186.4 515.2l67.2-67.2zM128 896h768v96H128z"
                  />
                </svg>
              ) : (
                <div className="download-spinner" />
              )}
            </button>
          </div>
        )}
      </div>
    );
  },
);

export default KeypointPlayer;
